import { useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { useAccountStore, deriveFavoriteHero } from '../../stores/account';
import { allHeroes } from '../../game/data/heroes';
import { WordMark } from '../../components/WordMark';
import { updateMyHandle } from '../../net/friendsClient';
import { EDGES } from '../../theme/tokens';

// Profile — local player identity + match record + cosmetics drawer (stub).
// All data is local-first via the account store. Cloud sync lands in R9.

export function ProfilePage() {
  const handle = useAccountStore((s) => s.handle);
  const setHandle = useAccountStore((s) => s.setHandle);
  const factionAllegiance = useAccountStore((s) => s.factionAllegiance);
  const setFactionAllegiance = useAccountStore((s) => s.setFactionAllegiance);
  const totalMatches = useAccountStore((s) => s.totalMatches);
  const wins = useAccountStore((s) => s.wins);
  const heroPlayCounts = useAccountStore((s) => s.heroPlayCounts);
  const resetStats = useAccountStore((s) => s.resetStats);

  const [editing, setEditing] = useState(false);
  const [draftHandle, setDraftHandle] = useState(handle);

  const winRate = totalMatches > 0 ? Math.round((wins / totalMatches) * 100) : 0;
  const favoriteHeroId = deriveFavoriteHero(heroPlayCounts);
  const favoriteHero = favoriteHeroId
    ? allHeroes.find((h) => h.id === favoriteHeroId)
    : null;
  const favoriteCount = favoriteHeroId ? heroPlayCounts[favoriteHeroId] : 0;

  function commitHandle() {
    setHandle(draftHandle);
    setEditing(false);
    // Push to server so friend search + crew display see the change.
    // Fire-and-forget — local state already wrote, server is a courtesy.
    updateMyHandle(draftHandle).catch(() => {});
  }

  function pickFaction(faction: 'cholo' | 'emo' | null) {
    setFactionAllegiance(faction);
  }

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-1/4 -top-1/4 h-[60vw] w-[60vw] max-h-[760px] max-w-[760px]"
        style={{
          background:
            factionAllegiance === 'emo'
              ? 'radial-gradient(circle, rgba(255,46,126,0.16) 0%, rgba(255,46,126,0) 70%)'
              : factionAllegiance === 'cholo'
                ? 'radial-gradient(circle, rgba(255,90,31,0.16) 0%, rgba(255,90,31,0) 70%)'
                : 'radial-gradient(circle, rgba(255,138,61,0.14) 0%, rgba(255,138,61,0) 70%)',
        }}
      />

      <section className="relative mx-auto flex h-full w-full max-w-6xl flex-col gap-xs overflow-y-auto px-md py-xs md:gap-lg md:px-lg md:py-md lg:max-w-5xl lg:gap-xl lg:py-xl">
        <header className="flex items-center justify-between">
          <span className="md:hidden">
            <WordMark size="sm" />
          </span>
          <span className="hidden md:inline lg:hidden">
            <WordMark size="md" />
          </span>
          <span className="hidden lg:inline">
            <WordMark size="lg" />
          </span>
          <Link
            to="/menu"
            className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
          >
            ← menu
          </Link>
        </header>

        {/* Handle + faction allegiance — the identity block */}
        <section
          className="flex flex-col gap-xs rounded-md border bg-stucco1 p-sm md:gap-sm md:p-lg lg:p-xl"
          style={{ borderColor: EDGES.soft }}
        >
          <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
            /profile · handle
          </p>
          {editing ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                commitHandle();
              }}
              className="flex flex-col gap-sm md:flex-row md:items-center"
            >
              <input
                value={draftHandle}
                onChange={(e) => setDraftHandle(e.target.value)}
                maxLength={24}
                autoFocus
                className={`flex-1 rounded-sm border bg-asphalt px-md py-sm font-cholo text-[28px] outline-none focus:border-sodium md:text-[40px] lg:text-[48px] ${
                  factionAllegiance === 'emo' ? 'text-emo' : 'text-cholo'
                }`}
                style={{ borderColor: 'rgba(245,236,214,0.15)' }}
              />
              <button
                type="submit"
                className="min-h-[44px] rounded-sm bg-sodium px-lg font-data text-[12px] uppercase tracking-[0.2em] text-sharpie hover:bg-sodiumGlow md:text-[13px]"
              >
                Save
              </button>
            </form>
          ) : (
            <div className="flex items-baseline justify-between gap-md">
              <h1
                className={`truncate font-cholo text-[22px] leading-none md:text-[48px] lg:text-[72px] ${
                  factionAllegiance === 'emo' ? 'text-emo font-emo' : 'text-cholo'
                }`}
              >
                {handle}
              </h1>
              <button
                onClick={() => {
                  setDraftHandle(handle);
                  setEditing(true);
                }}
                className="shrink-0 font-data text-[13px] uppercase tracking-[0.2em] text-marker hover:text-bone md:text-[12px]"
              >
                ↻ edit
              </button>
            </div>
          )}

          {/* Faction allegiance toggles */}
          <div className="flex flex-col gap-sm md:flex-row md:items-center md:gap-md">
            <p className="font-data text-[12px] uppercase tracking-[0.2em] text-marker md:text-[11px]">
              allegiance
            </p>
            <div className="flex gap-xs md:gap-sm">
              <FactionToggle
                label="Cholo"
                active={factionAllegiance === 'cholo'}
                tone="cholo"
                onClick={() => pickFaction(factionAllegiance === 'cholo' ? null : 'cholo')}
              />
              <FactionToggle
                label="Emo"
                active={factionAllegiance === 'emo'}
                tone="emo"
                onClick={() => pickFaction(factionAllegiance === 'emo' ? null : 'emo')}
              />
              {factionAllegiance && (
                <button
                  onClick={() => pickFaction(null)}
                  className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite hover:text-marker"
                >
                  clear
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Stats — match record */}
        <section
          className="rounded-md border bg-stucco1 p-xs md:p-lg lg:p-xl"
          style={{ borderColor: EDGES.soft }}
        >
          <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
            record
          </p>
          {totalMatches === 0 ? (
            <p className="mt-sm text-[14px] text-marker md:text-[16px]">
              No matches yet. Your record fills in as you play.
            </p>
          ) : (
            <div className="mt-xs grid grid-cols-3 gap-md md:mt-md md:gap-lg">
              <StatCell label="Matches" value={String(totalMatches)} />
              <StatCell label="Wins" value={String(wins)} accent="glowStick" />
              <StatCell label="Win rate" value={`${winRate}%`} accent="sodium" />
            </div>
          )}
          {favoriteHero && (
            <div className="mt-xs flex items-baseline gap-sm border-t border-stucco3/40 pt-xs md:mt-md md:gap-md md:pt-md">
              <p className="font-data text-[12px] uppercase tracking-[0.2em] text-marker md:text-[11px]">
                favorite hero
              </p>
              <p
                className={`flex-1 text-[14px] leading-tight md:text-[16px] ${
                  favoriteHero.faction === 'emo' ? 'font-emo text-emo' : 'font-cholo text-cholo'
                }`}
              >
                {favoriteHero.name}
              </p>
              <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
                {favoriteCount}× picked
              </p>
            </div>
          )}
        </section>

        {/* Cosmetics drawer — placeholder per MVP spec */}
        <section
          className="rounded-md border bg-stucco1 p-xs md:p-lg lg:p-xl"
          style={{ borderColor: EDGES.soft }}
        >
          <div className="flex items-baseline justify-between gap-md">
            <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
              cosmetics
            </p>
            <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
              coming soon
            </p>
          </div>
          <p className="mt-xs text-[12px] text-marker md:mt-sm md:text-[15px]">
            Card backs, board frames, hero portrait variants, victory animations. The
            cosmetic engine ships in v2.1 — earnable only, no purchases.
          </p>
        </section>

        {/* Danger zone — reset stats */}
        {totalMatches > 0 && (
          <section className="text-center">
            <motion.button
              whileTap={{ scale: 0.96 }}
              onClick={() => {
                if (confirm('Reset match record? This cannot be undone.')) {
                  resetStats();
                }
              }}
              className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite hover:text-aerosolRed md:text-[11px]"
            >
              reset record
            </motion.button>
          </section>
        )}
      </section>
    </main>
  );
}

interface FactionToggleProps {
  label: string;
  active: boolean;
  tone: 'cholo' | 'emo';
  onClick: () => void;
}

function FactionToggle({ label, active, tone, onClick }: FactionToggleProps) {
  const isEmo = tone === 'emo';
  return (
    <button
      onClick={onClick}
      className={`min-h-[44px] rounded-sm border px-md font-data text-[13px] uppercase tracking-[0.2em] transition-colors md:text-[12px] ${
        active
          ? isEmo
            ? 'border-emo bg-emo/15 text-emo'
            : 'border-cholo bg-cholo/15 text-cholo'
          : 'border-stucco3 bg-stucco2 text-marker hover:text-bone'
      }`}
      style={{
        borderColor: active ? (isEmo ? '#ff2e7e' : '#ff5a1f') : undefined,
      }}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

interface StatCellProps {
  label: string;
  value: string;
  accent?: 'sodium' | 'glowStick' | 'aerosolRed';
}

function StatCell({ label, value, accent }: StatCellProps) {
  const accentClass =
    accent === 'sodium'
      ? 'text-sodium'
      : accent === 'glowStick'
        ? 'text-glowStick'
        : accent === 'aerosolRed'
          ? 'text-aerosolRed'
          : 'text-bone';
  return (
    <div className="flex flex-col items-start gap-[2px]">
      <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
        {label}
      </p>
      <p className={`font-data text-[20px] font-bold leading-none md:text-[32px] lg:text-[40px] ${accentClass}`}>
        {value}
      </p>
    </div>
  );
}
